import { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { ErrorMessage, Field } from 'formik';
import { FormHelperText, Input, InputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { WriterlyIcon } from '@shared/Icon/Icon';

export const WriterlyFormikField = memo(
  ({
    error,
    touched,
    inputRef,
    placeholder = '',
    name,
    value = '',
    onChange,
    charCount,
    endAdornment,
    startAdornment,
    errorProps,
    customSx = {},
    passwordIconClasses = '',
    ...other
  }) => {
    const [inputVisibility, setInputVisibility] = useState(false);

    const theme = useTheme();

    const handleInputVisibility = () => {
      const input =
        document?.getElementById(
          other?.type && other.type?.includes('password') && name
        ) || null;
      if (input) {
        if (input.type === 'text') {
          setInputVisibility(false);
          input.type = 'password';
        } else {
          setInputVisibility(true);
          input.type = 'text';
        }
      }
    };

    return (
      <div className="flex items-center relative">
        <Field name={name} className="w-full">
          {({ field }) => {
            return (
              <div className="w-full relative">
                <Input
                  {...other}
                  {...field}
                  id={name}
                  ref={inputRef}
                  name={name}
                  value={value}
                  onChange={onChange}
                  placeholder={placeholder}
                  className={`w-full ${other?.className} ${
                    other?.variant !== 'outlined' && '!px-3 !py-2'
                  }`}
                  sx={{
                    '& .MuiInputBase': {
                      '& .MuiInput-root': {
                        '::before': {
                          display: 'none !important',
                        },
                      },
                    },
                    ...customSx,
                  }}
                  error={touched ? Boolean(error) : false}
                  endAdornment={
                    endAdornment ? (
                      endAdornment
                    ) : (
                      <InputAdornment
                        position="end"
                        id={`endAdornment-${name}`}
                      >
                        {charCount && `${value.length}/${charCount}`}
                      </InputAdornment>
                    )
                  }
                  startAdornment={startAdornment}
                />
                <ErrorMessage
                  name={name}
                  component={() => (
                    <FormHelperText
                      id={`error-${name}`}
                      className={`absolute leading-3 ${errorProps?.classes}`}
                      style={{ color: theme.palette.various.errorMessages }}
                    >
                      {error}
                    </FormHelperText>
                  )}
                />
              </div>
            );
          }}
        </Field>
        {other?.type && other?.type === 'password' && (
          <WriterlyIcon
            className={`cursor-pointer absolute ${
              other?.variant === 'outlined' ? 'right-2' : '-right-1'
            } ${passwordIconClasses}`}
            iconName={
              inputVisibility ? 'writerlyOpenEyeIcon' : 'writerlyClosedEyeIcon'
            }
            onClick={() => handleInputVisibility()}
            width="20px"
            height="20px"
            stroke={theme.palette.text.secondary}
            dataTestId={`form-eyeIconVisibility${name}`}
          />
        )}
      </div>
    );
  }
);

WriterlyFormikField.displayName = 'WriterlyFormikField';

WriterlyFormikField.propTypes = {
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
  value: PropTypes.string,
  inputRef: PropTypes.any,
  onChange: PropTypes.func,
  charCount: PropTypes.number,
  endAdornment: PropTypes.node,
  startAdornment: PropTypes.node,
  errorProps: PropTypes.any,
  customSx: PropTypes.object,
  passwordIconClasses: PropTypes.string,
};
